import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { trackAiPromptSubmitted, trackAiUpgradePromptShown } from '../../analytics';
import { editDeckWithAi } from '../../api/slides';
import { PitchSlide } from '../../data/slides';
import { canUseAi } from '../../data/subscriptions';
import { appTheme } from '../../styles/theme';
import MentionPromptEditor from './MentionPromptEditor';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
`;

const Composer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 8px 8px 12px;
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.surface};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${appTheme.green};
    box-shadow: 0 0 0 2px rgba(19, 78, 68, 0.15);
  }
`;

const EditorWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const SendButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: ${appTheme.radiusPill};
  background: ${appTheme.green};
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;

  &:hover:not(:disabled) {
    background: ${appTheme.greenHover};
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${appTheme.green};
    outline-offset: 2px;
  }
`;

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M3.478 2.405a.75.75 0 0 0-.926.94l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94l18-7.5a.75.75 0 0 0 0-1.392l-18-7.5Z" />
  </svg>
);

const Upsell = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${appTheme.textMuted};
`;

const UpgradeLink = styled(Link)`
  align-self: flex-start;
  font-size: 0.8125rem;
  font-weight: 700;
  color: ${appTheme.green};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.dangerText};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: ${appTheme.textMuted};
`;

interface AiSlidePanelProps {
  slides: PitchSlide[];
  deckName: string;
  plan?: string | null;
  onDeckChange: (slides: PitchSlide[], deckName?: string) => void;
}

const AiSlidePanel: React.FC<AiSlidePanelProps> = ({ slides, deckName, plan, onDeckChange }) => {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const aiEnabled = canUseAi(plan);
  const upgradeTrackedRef = useRef(false);

  useEffect(() => {
    if (!aiEnabled && !upgradeTrackedRef.current) {
      trackAiUpgradePromptShown();
      upgradeTrackedRef.current = true;
    }
  }, [aiEnabled]);

  const handleGenerate = async () => {
    if (!prompt.trim() || isGenerating || !aiEnabled) {
      return;
    }
    setIsGenerating(true);
    setError(null);
    try {
      const result = await editDeckWithAi({
        prompt: prompt.trim(),
        deckName,
        slides,
      });
      onDeckChange(result.slides, result.deckName);
      setPrompt('');
      trackAiPromptSubmitted({
        plan: plan ?? 'free',
        slide_count: slides.length,
        success: true,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('editor.ai.updateError');
      trackAiPromptSubmitted({
        plan: plan ?? 'free',
        slide_count: slides.length,
        success: false,
        error_message: message,
      });
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const inputsDisabled = !aiEnabled || isGenerating;
  const sendDisabled = inputsDisabled || !prompt.trim();

  return (
    <Panel>
      <Composer>
        <EditorWrap>
          <MentionPromptEditor
            slides={slides}
            value={prompt}
            onChange={setPrompt}
            disabled={inputsDisabled}
            borderless
            placeholder={t('editor.ai.placeholder')}
            onSubmit={handleGenerate}
            submitDisabled={sendDisabled}
          />
        </EditorWrap>
        <SendButton
          type="button"
          onClick={handleGenerate}
          disabled={sendDisabled}
          aria-label={isGenerating ? t('editor.ai.updatingDeck') : t('editor.ai.applyToDeck')}
          title={isGenerating ? t('editor.ai.updatingDeckTitle') : t('editor.ai.applyToDeckTitle')}
        >
          <SendIcon />
        </SendButton>
      </Composer>
      <Hint>{t('editor.ai.hint')}</Hint>
      {!aiEnabled && (
        <>
          <Upsell>{t('editor.ai.upsell')}</Upsell>
          <UpgradeLink to="/subscription">{t('editor.ai.upgradeLink')}</UpgradeLink>
        </>
      )}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {isGenerating && <Hint>{t('editor.ai.generating', { count: slides.length })}</Hint>}
    </Panel>
  );
};

export default AiSlidePanel;
